import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useStorage } from '@vueuse/core';
import type { AxiosResponse } from 'axios';
import { userApi } from '@/entities/User';
import type { User } from '@/entities/User';
import type { Branch } from '@/entities/Branch/@x/User';
import { RoleEnum } from '../model/types';

interface UserResponse {
  success: boolean;
  data: User;
}

interface UserState {
  user: User | null;
  isLoading: boolean;
}

function loadUserState(): UserState {
  const data = localStorage.getItem('userStore');
  if (!data) {
    return { user: null, isLoading: false };
  }

  try {
    return JSON.parse(data) as UserState;
  } catch {
    localStorage.removeItem('userStore');
    return { user: null, isLoading: false };
  }
}

const useUserStore = defineStore('user', () => {
  const initialState = loadUserState();
  const user = ref<User | null>(initialState.user);
  const isLoading = ref(initialState.isLoading);
  const selectedBranchId = useStorage<string | null>('selectedBranchId', null);

  const id = computed(() => user.value?.id ?? null);
  const name = computed(() => user.value?.name ?? null);
  const roles = computed(() => user.value?.positions ?? []);
  const isSuperuser = computed(() => user.value?.role === RoleEnum.SUPERUSER);
  const memberships = computed(() => user.value?.memberships ?? []);
  const memberId = computed(() => {
    const currentBranchId = Number(selectedBranchId.value);
    if (!currentBranchId) return null;
    return user.value?.memberships?.find((m) => m.branchId === currentBranchId)?.id ?? null;
  });
  const branches = computed(() => {
    return (
      user.value?.memberships
        ?.filter((membership) => membership.branch)
        .map((membership) => membership.branch) ?? []
    );
  });
  const accessibleBranches = computed<Branch[]>(() =>
    isSuperuser.value ? (user.value?.branches ?? []) : []
  );
  const isMemberOfSelectedBranch = computed(() => {
    const currentBranchId = Number(selectedBranchId.value);
    if (!currentBranchId) return false;
    return user.value?.memberships?.some((m) => m.branchId === currentBranchId) ?? false;
  });

  function setUser(userData: User) {
    user.value = userData;
    saveState();
  }

  function saveState() {
    localStorage.setItem('userStore', JSON.stringify({ user: user.value, isLoading: isLoading.value }));
  }

  async function me(): Promise<AxiosResponse<UserResponse>> {
    try {
      isLoading.value = true;
      const response = await userApi.getCurrentUser();

      if (response.data.success) {
        setUser(response.data.data);
      }

      return response;
    } catch (error) {
      clearUser();
      throw error;
    } finally {
      isLoading.value = false;
    }
  }

  function clearUser() {
    localStorage.removeItem('userStore');
    user.value = null;
    isLoading.value = false;
  }

  function $reset() {
    user.value = null;
    isLoading.value = false;
  }

  return {
    user,
    isLoading,
    id,
    name,
    roles,
    isSuperuser,
    memberships,
    memberId,
    branches,
    accessibleBranches,
    isMemberOfSelectedBranch,
    setUser,
    saveState,
    me,
    clearUser,
    $reset,
  };
});

export default useUserStore;
